import React, { forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { TextField, Checkbox, ListItemText, Chip, Popper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ResponsiveContainer } from 'recharts';
import { subDays, subMonths, subWeeks, subYears } from 'date-fns';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import { PopupToolbarChip } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

export function isNumber(value) { return typeof value === 'number' && Number.isFinite(value); }

export function toMs(value) {
    if (!value) return NaN;
    if (value instanceof Date) return value.getTime();

    const time = new Date(value).getTime();

    return Number.isFinite(time) ? time : NaN;
}

export function normalizeNumber(value) {
    if (isNumber(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
    if (typeof value === 'boolean') return value ? 1 : 0;

    return null;
}

export function colorForIndex(index, count) {
    if (count <= 1) return '#252f3e';

    return 'hsl(' + ((index * 60) % 360) + ', 70%, 40%)';
}

export function buildDateBounds(rows, tsKey = 'ts') {
    if (!Array.isArray(rows) || rows.length === 0) return { minDate: null, maxDate: null };

    const sorted = rows
        .filter(Boolean)
        .filter((row) => row && row[tsKey])
        .sort((a, b) => toMs(a[tsKey]) - toMs(b[tsKey]));

    if (!sorted.length) return { minDate: null, maxDate: null };

    const minDate = new Date(sorted[0][tsKey]);
    const maxDate = new Date(sorted[sorted.length - 1][tsKey]);

    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return { minDate: null, maxDate: null };

    return { minDate, maxDate };
}

export function buildDisabledQuickOptions(minDate, maxDate) {
    if (!minDate || !maxDate) {
        return { ALL: true, '1Y': true, '1M': true, '1W': true, '1D': true };
    }

    const minTime = minDate.getTime();

    return {
        ALL: false,
        '1Y': subYears(maxDate, 1).getTime() < minTime,
        '1M': subMonths(maxDate, 1).getTime() < minTime,
        '1W': subWeeks(maxDate, 1).getTime() < minTime,
        '1D': subDays(maxDate, 1).getTime() < minTime
    };
}

export function formatPercent(value) {
    if (value == null || Number.isNaN(value)) return '-';

    return formatLabelValue(value, { thousandsSeparator: false, decimalPlaces: 2 }) + '%';
}

export function pickUom(uomMap, key) {
    if (!uomMap || !key) return null;

    const root = String(key).includes('.') ? String(key).split('.')[0] : null;

    if (typeof uomMap.get === 'function' && typeof uomMap.has === 'function') {
        if (uomMap.has(key)) return uomMap.get(key) ?? null;
        if (root && uomMap.has(root)) return uomMap.get(root) ?? null;
        return null;
    }

    if (Object.prototype.hasOwnProperty.call(uomMap, key)) return uomMap[key];
    if (root && Object.prototype.hasOwnProperty.call(uomMap, root)) return uomMap[root];

    return null;
}

export function getChartTooltipProps(content) {
    return {
        content,
        cursor: { fill: 'transparent' },
        allowEscapeViewBox: { x: true, y: true },
        wrapperStyle: {
            zIndex: 5000,
            pointerEvents: 'none'
        }
    };
}

export function getChartPortalTooltipProps(content, chartRef, options = {}) {
    return {
        content: (props) => (
            <OptimizationPipelineChartPortalTooltip
                {...props}
                chartRef={chartRef}
                content={content}
                offsetX={options.offsetX}
                offsetY={options.offsetY}
                zIndex={options.zIndex}
            />
        ),
        cursor: { fill: 'transparent' },
        allowEscapeViewBox: { x: true, y: true },
        wrapperStyle: {
            display: 'none',
            pointerEvents: 'none'
        }
    };
}

export function OptimizationPipelineChartPortalTooltip(props) {
    const {
        active,
        payload,
        label,
        coordinate,
        chartRef,
        content,
        offsetX = 16,
        offsetY = 0,
        zIndex = 7000
    } = props;

    if (!active || !payload?.length || !coordinate || typeof document === 'undefined') return null;

    const chartNode = chartRef?.current;
    if (!chartNode || typeof chartNode.getBoundingClientRect !== 'function') return null;

    const rect = chartNode.getBoundingClientRect();
    const left = rect.left + Number(coordinate.x || 0) + offsetX;
    const top = rect.top + Number(coordinate.y || 0) + offsetY;

    const tooltipContent = typeof content === 'function'
        ? content({ active, payload, label, coordinate })
        : content;

    if (!tooltipContent) return null;

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', left: left, top: top, transform: 'translateY(-50%)', zIndex: zIndex, pointerEvents: 'none' }}>
            {tooltipContent}
        </div>,
        document.body
    );
}

function ChartAutocompletePopper(props) {
    return <Popper {...props} placement='bottom-start' style={{ ...props.style, zIndex: 5000 }} />;
}

export const OptimizationPipelineChartFrame = forwardRef(function OptimizationPipelineChartFrame(props, ref) {
    const { children, height = 440, minHeight = 320, className, overflowVisible = false } = props;

    return (
        <div ref={ref} className={'w-full min-h-0 ' + (overflowVisible ? 'overflow-visible ' : 'overflow-hidden ') + (className || '')} style={{ height: height, minHeight: minHeight }}>
            <ResponsiveContainer width='100%' height={height}>{children}</ResponsiveContainer>
        </div>
    );
});

export function OptimizationPipelineChartEmptyState(props) {
    const { children, minHeight = 320, className } = props;

    return (
        <div className={'w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium ' + (className || '')} style={{ minHeight: minHeight }}>
            {children}
        </div>
    );
}

export function OptimizationPipelineChartModeToggle(props) {
    const { value, onChange, options = [], disabled = false } = props;

    return (
        <>
            {options.map((option) => (
                <PopupToolbarChip key={option.value} label={option.label} active={value === option.value} disabled={disabled || option.disabled} onClick={() => onChange?.(option.value)} />
            ))}
        </>
    );
}

export function OptimizationPipelineChartTypeToggle(props) {
    const { value, onChange, disabled = false } = props;

    return <PopupToolbarChip label={value === 'line' ? 'Bar' : 'Line'} active={false} disabled={disabled} onClick={() => onChange?.(value === 'line' ? 'bar' : 'line')} className='min-w-40' />;
}

export function OptimizationPipelineChartViewToggle(props) {
    const { isChartView, onChange, disabled = false } = props;

    return <PopupToolbarChip label={isChartView ? 'Table' : 'Chart'} active={false} disabled={disabled} onClick={() => onChange?.(!isChartView)} className='min-w-48' />;
}

export function OptimizationPipelineChartTooltip(props) {
    const { title, children, className, minWidth = 220, maxWidth = 420 } = props;

    return (
        <div className={'flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg ' + (className || '')} style={{ minWidth: minWidth, maxWidth: maxWidth }}>
            {title ? <div className='mb-2 font-semibold text-[11px] opacity-80'>{title}</div> : null}
            {children}
        </div>
    );
}

export function OptimizationPipelineChartKeySelector(props) {
    const { options = [], value = [], onChange, label = 'Search and select', placeholder = 'Select keys', maxSelected = null, getColor, getChipStyle, renderChipActions, className, onHoverKeyChange } = props;
    const normalizedValue = Array.isArray(value) ? value : [];

    function handleChange(event, nextValue) {
        const next = Array.isArray(nextValue) ? nextValue : [];
        onChange?.(maxSelected ? next.slice(0, maxSelected) : next);
    }

    function handleMouseEnter(option) {
        if (typeof onHoverKeyChange === 'function') {
            onHoverKeyChange(option);
        }
    }

    function handleMouseLeave() {
        if (typeof onHoverKeyChange === 'function') {
            onHoverKeyChange(null);
        }
    }

    function renderKeyLabel(option) {
        const parts = String(option).split('.');
        const parent = parts.length > 1 ? parts[0] : null;
        const child = parts.length > 1 ? parts.slice(1).join('.') : option;

        if (!parent) return option;

        return <span><span className='font-semibold opacity-30'>{parent + '.'}</span>{child}</span>;
    }

    return (
        <div className={'w-full min-w-0 mt-8 ' + (className || '')}>
            <Autocomplete
                multiple
                options={options}
                value={normalizedValue}
                onChange={handleChange}
                disableCloseOnSelect
                disablePortal={false}
                PopperComponent={ChartAutocompletePopper}
                getOptionLabel={(option) => option}
                renderOption={(option, { selected }) => (
                    <div className='w-full flex items-center' onMouseEnter={() => handleMouseEnter(option)} onMouseLeave={handleMouseLeave}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        <ListItemText primary={renderKeyLabel(option)} />
                    </div>
                )}
                renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => {
                    const color = typeof getColor === 'function' ? getColor(option, index, tagValue) : colorForIndex(index, tagValue.length);
                    const chipStyle = typeof getChipStyle === 'function' ? getChipStyle(option, index, tagValue) : null;
                    const tagProps = getTagProps({ index });

                    return (
                        <div key={option + '_' + index} className='flex items-center' onMouseEnter={() => handleMouseEnter(option)} onMouseLeave={handleMouseLeave}>
                            <Chip {...tagProps} size='small' label={option} style={{ padding: '5px 2px 5px 5px', fontSize: '12px', border: '2px solid ' + color, zIndex: 10, ...(chipStyle || {}) }} />
                            {typeof renderChipActions === 'function' ? renderChipActions({ key: option, option, index, color }) : null}
                        </div>
                    );
                })}
                renderInput={(params) => {
                    const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;

                    return (
                        <TextField
                            {...filteredParams}
                            variant='outlined'
                            label={label}
                            placeholder={placeholder}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ ...extractedInputProps, style: { minHeight: 40, alignItems: 'center' } }}
                            inputProps={{ ...extractedInnerInputProps, style: { padding: '10px 10px', fontSize: '13px', lineHeight: 1.5, minWidth: 120 } }}
                        />
                    );
                }}
            />
        </div>
    );
}
